
import { FieldArray } from 'react-final-form-arrays';
import { Field, useForm } from 'react-final-form';
import { useState, useRef, useEffect, useContext } from 'react';
import { v1 as uuidv1 } from "uuid";
import { FiUpload } from 'react-icons/fi';
import styles from './GstDetails.module.scss';
import { ApplicationContextType, UnderwritingFormStatus, UnderwritingSteps, uploadStates } from '../../../../interfaces/interfaces';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import { RiAddFill as AddIcon, RiLinksLine, RiErrorWarningFill } from 'react-icons/ri'
import { gstDetailInitialState, UnderwritingFormContext } from '../../context/UnderwritingFormContext';
import { GSTAccountList, GSTProgressStatus, isValidGstin, cleanGstin, cleanUsername, type GSTAccountRecord } from '../../../../components/GSTConnection';
import { useGstReconSocket, type GstJobUpdate } from '../../../../components/Hooks/useGstReconSocket';
import GstAccountDetail from './GstAccountDetail';
import GstCertificateUpload from './GstCertificateUpload';
import LendingConnectGst from './LendingConnectGst';
import UploadGstinListModal from '../../../GSTReconciliation/setup/UploadGstinListModal';
import { GstRestS } from '../../../../rest/gst-rest-service';
import gstStyles from '../../../GSTReconciliation/GSTReconciliation.module.scss';

type Props = {
  formCompleteStatus: {
    [x: string]: UnderwritingFormStatus
  },
  newUploadedDocIds: number[],
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[],
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  gstApiErrors?: any;
  setGstApiErrors: React.Dispatch<React.SetStateAction<any>>;
  formContext?: any;
  formType: ApplicationContextType
}

const GstDetails = ({ formCompleteStatus, newUploadedDocIds, deleteDocIds, setDeleteDocIds, setNewUploadedDocIds, setGstApiErrors, gstApiErrors, formContext, formType }: Props) => {
  const { change: changeForm } = useForm();
  const [connectModalIndex, setConnectModalIndex] = useState<number | null>(null);
  const [showUploadListModal, setShowUploadListModal] = useState(false);
  const { underwritingFileJobs, sendFileJobToApi } = useContext(
    formContext ? formContext : UnderwritingFormContext
  ) as any;

  // While a just-connected GSTIN's gst_fetch_data job is still running, show
  // a "fetching" status on its row instead of jumping straight to
  // "Connected" — same real-time signal Supplier Intelligence uses, just
  // without the multi-stage fake phase animation: one job, one status.
  const [activeGstJob, setActiveGstJob] = useState<{ gstin: string; fetchJobId: string } | null>(null);

  const handleGstJobUpdate = (update: GstJobUpdate) => {
    if (
      update.jobType !== 'gst_fetch_data' ||
      !activeGstJob ||
      String(update.jobId) !== activeGstJob.fetchJobId
    ) {
      return;
    }
    if (update.status === 'completed' || update.status === 'completed_with_errors' || update.status === 'failed') {
      setActiveGstJob(null);
    }
  };

  useGstReconSocket(handleGstJobUpdate);

  return (
    <div className={styles.sectionFieldsContainer}>
      <FieldArray name='gstStatements'>
        {({ fields, meta }) => {
          const statements = fields.value || [];
          const hasAnyGstin = statements.some((s: any) => s?.gstin);

          // ── Seed gstStatements[] from backend credentials (single source of truth) ──
          // On mount, fetch GET /gst/credentials and push any GSTINs that exist in the
          // backend but aren't in the form yet (e.g. connected from Supplier Intelligence).
          // Runs once per mount; the seededRef guard prevents duplicate pushes in StrictMode.
          const seededRef = useRef(false);
          const syncCredentialsFromBackend = async (options?: {
            replaceIndex?: number;
            draft?: { gstin: string; username: string };
          }) => {
            try {
              // Blank placeholder rows (gstin never filled in) serve no purpose
              // once they're not mid-edit — drop them up front so they never
              // get displayed or saved back to the form. Skip replaceIndex:
              // that row is legitimately blank mid-reconnect.
              const prePruneStatements = fields.value || [];
              for (let index = prePruneStatements.length - 1; index >= 0; index--) {
                if (index === options?.replaceIndex) continue;
                if (!prePruneStatements[index]?.gstin) {
                  fields.remove(index);
                }
              }

              const res = await GstRestS.getCredentials("investment");
              const creds = res?.data ?? [];
              if (!Array.isArray(creds) || creds.length === 0) return;

              const statements = fields.value || [];
              const draftGstin = options?.draft?.gstin
                ? cleanGstin(options.draft.gstin)
                : "";
              const draftUsername = options?.draft?.username
                ? cleanUsername(options.draft.username)
                : "";
              const existingGstins = new Set(
                statements
                  .map((s: any, index: number) =>
                    index === options?.replaceIndex ? null : s?.gstin
                  )
                  .filter(Boolean)
              );
              // Re-add the draft gstin only when reconnecting an existing row
              // (replaceIndex defined) — that row's slot was just excluded by
              // index above regardless of its current gstin, so without this
              // it would look "new" and get pushed as a duplicate alongside
              // the row already being updated below. When adding a brand new
              // GSTIN (replaceIndex undefined), the draft must stay eligible
              // for the push below — that's the only way a credential the
              // backend already created (e.g. modal closed before OTP
              // success) ever makes it into the form without a refresh.
              if (draftGstin && options?.replaceIndex !== undefined) {
                existingGstins.add(draftGstin);
              }

              statements.forEach((s: any, index: number) => {
                const gstinToMatch =
                  index === options?.replaceIndex && draftGstin
                    ? draftGstin
                    : s?.gstin;
                if (!gstinToMatch) return;
                const match = creds.find((c: any) => c.gstin === gstinToMatch);
                if (!match) return;
                changeForm(`gstStatements[${index}].credentialId`, match.credentialId);
                changeForm(`gstStatements[${index}].gstin`, match.gstin);
                changeForm(`gstStatements[${index}].username`, match.gstUsername || "");
                changeForm(`gstStatements[${index}].sessionStatus`, match.sessionStatus);
                changeForm(`gstStatements[${index}].isVerified`, match.sessionStatus === "active");
                changeForm(`gstStatements[${index}].fetchedFromPan`, !!match.fetchedFromPan);
              });

              if (options?.replaceIndex !== undefined && draftGstin) {
                const match = creds.find((c: any) => c.gstin === draftGstin);
                if (!match && draftUsername) {
                  changeForm(`gstStatements[${options.replaceIndex}].gstin`, draftGstin);
                  changeForm(`gstStatements[${options.replaceIndex}].username`, draftUsername);
                }
              }

              creds
                .filter((c: any) => c.gstin && !existingGstins.has(c.gstin))
                .forEach((c: any) => {
                  // Re-check fields.value fresh, not just the existingGstins
                  // snapshot taken at the top of this call — if two syncs
                  // overlap (e.g. the mount-time sync and a connect-triggered
                  // sync racing each other), the snapshot can go stale and
                  // both would otherwise push the same gstin twice.
                  const alreadyPresent = (fields.value || []).some((s: any) => s?.gstin === c.gstin);
                  if (alreadyPresent) return;
                  fields.push({
                    ...gstDetailInitialState,
                    statementJobId: uuidv1(),
                    credentialId: c.credentialId,
                    gstin: c.gstin,
                    username: c.gstUsername || "",
                    sessionStatus: c.sessionStatus,
                    isVerified: c.sessionStatus === "active",
                    fetchedFromPan: !!c.fetchedFromPan,
                  });
                });
            } catch (error) {
              console.log("error syncing gstStatements from credentials", error);
            }
          };

          const closeConnectModal = async (draft?: { gstin: string; username: string }) => {
            await syncCredentialsFromBackend({
              replaceIndex: connectModalIndex >= 0 ? connectModalIndex : undefined,
              draft,
            });
            setConnectModalIndex(null);
          };

          const getCredentialForGstin = async (gstin: string) => {
            try {
              const res = await GstRestS.getCredentials("investment");
              const creds = res?.data ?? [];
              if (!Array.isArray(creds)) return null;
              return creds.find((c: any) => c.gstin === gstin) ?? null;
            } catch {
              return null;
            }
          };

          useEffect(() => {
            if (seededRef.current) return;
            seededRef.current = true;
            syncCredentialsFromBackend();
          }, []);

          if (!hasAnyGstin) {
            return (
              <>
                {meta.submitFailed && meta.error ? (
                  <div id="gstStatements" className={styles.errorMainContainer}>
                    <div className={styles.errorContainer}>
                      <div className={styles.errorTitle}>
                        <RiErrorWarningFill className={styles.errorTitleIcon} />
                        <div className={styles.errorTitleText}>
                          {meta?.error}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div
                  className={`${gstStyles.footerBtns} ${styles.emptyStateBtns}`}
                >
                  <MeritoButton
                    type="primary-1"
                    size="lg"
                    icon={<RiLinksLine />}
                    btnText="Connect Manually"
                    isRounded
                    onClick={() => setConnectModalIndex(-1)}
                  />
                  <MeritoButton
                    type="secondary-2"
                    size="lg"
                    isRounded
                    icon={<FiUpload />}
                    btnText="Upload List"
                    onClick={() => setShowUploadListModal(true)}
                  />
                </div>

                {showUploadListModal && (
                  <UploadGstinListModal
                    onClose={() => setShowUploadListModal(false)}
                    onImported={(records) => {
                      records.forEach((record) => {
                        const alreadyPresent = (fields.value || []).some((s: any) => s?.gstin === record.gstin);
                        if (alreadyPresent) return;
                        fields.push({
                          ...gstDetailInitialState,
                          statementJobId: uuidv1(),
                          gstin: record.gstin,
                          username: record.username,
                        });
                      });
                      (formContext as any)?.fetchGstAnalysisJobs?.();
                      setShowUploadListModal(false);
                    }}
                  />
                )}

                {connectModalIndex !== null && (
                  <LendingConnectGst
                    onClose={closeConnectModal}
                    onConnected={async (values) => {
                      const credential = await getCredentialForGstin(values.gstin);
                      const alreadyPresent = (fields.value || []).some((s: any) => s?.gstin === values.gstin);
                      if (connectModalIndex === -1 && !alreadyPresent) {
                        fields.push({
                          ...gstDetailInitialState,
                          credentialId: credential?.credentialId ?? null,
                          gstin: values.gstin,
                          username: credential?.gstUsername || values.username,
                          jobId: values.jobId,
                          statementJobId: values.statementJobId,
                          sessionStatus: credential?.sessionStatus,
                          isVerified: credential?.sessionStatus === "active",
                        });
                      }
                      if (values.jobId) {
                        setActiveGstJob({ gstin: values.gstin, fetchJobId: values.jobId });
                      }
                      // A successful connect can trigger PAN-based discovery of
                      // sibling GSTINs server-side (see fetchGSTINFromPAN in
                      // jigsaw-api). Re-sync so those show up now instead of
                      // only after a page refresh. The draft hint keeps this
                      // row from being pushed a second time by the sync.
                      await syncCredentialsFromBackend({
                        draft: { gstin: values.gstin, username: values.username },
                      });
                      (formContext as any)?.fetchGstAnalysisJobs?.();
                      setConnectModalIndex(null);
                    }}
                    initialGstin={connectModalIndex >= 0 ? statements[connectModalIndex]?.gstin : undefined}
                    initialUsername={connectModalIndex >= 0 ? statements[connectModalIndex]?.username : undefined}
                    formDetails={{
                      gstin: connectModalIndex >= 0 ? (statements[connectModalIndex]?.gstin || '') : '',
                      username: connectModalIndex >= 0 ? (statements[connectModalIndex]?.username || '') : '',
                      statementJobId: connectModalIndex >= 0 ? (statements[connectModalIndex]?.statementJobId || '') : '',
                      formType: (formContext as any)?.extraFormDetails?.formType,
                      formId: (formContext as any)?.extraFormDetails?.id,
                      monthYear: (formContext as any)?.extraFormDetails?.monthYear,
                      fieldName: connectModalIndex >= 0 ? `gstStatements[${connectModalIndex}]` : '',
                    }}
                  />
                )}
              </>
            );
          }

          const records: GSTAccountRecord[] = statements
            // Blank placeholder rows (gstin never filled in) have nothing to
            // show — they're pruned from form state on sync, but filter them
            // here too in case a sync hasn't run yet this render.
            .map((statement: any, index: number) => ({ statement, index }))
            .filter(({ statement }) => !!statement?.gstin)
            .map(({ statement, index }: { statement: any; index: number }) => {
            const fieldName = `gstStatements[${index}]`;
            const gstin = statement?.gstin || '';
            const username = statement?.username || '';
            const isVerified = statement?.isVerified;
            const isOld = statement?.isOld;
            const isInActive = statement?.isInActive;
            const sessionStatus = statement?.sessionStatus;
            const isJobProcessing = activeGstJob?.gstin === gstin;
            const isReconnect = !isVerified && !isJobProcessing && !isInActive;
            const certFieldName = `${fieldName}.gstCertificate`;
            const certJob = underwritingFileJobs?.[UnderwritingSteps.company]?.[certFieldName];
            // underwritingFileJobs keys jobs by array index, not by GSTIN — if
            // gstStatements ever reorders (e.g. the placeholder-row pruning
            // above), a stale job can end up pointing at whatever row now
            // sits at that index. Guard against that by also requiring this
            // row to actually have a certificate of its own before trusting
            // the job's password-required status.
            const certRequiresPassword =
              certJob?.status === uploadStates.requiredPassword && !!statement?.gstCertificate;
            const hasCertificate = !!(statement?.gstCertificate?.uploadedFile || statement?.gstCertificate?.selectedFile);

            return {
              key: fieldName,
              gstin,
              username,
              fetchedFromPan: !!statement?.fetchedFromPan,
              isReconnect,
              renderUpload: !isOld
                ? () => (
                    <GstCertificateUpload
                      fieldName={`${fieldName}.gstCertificate`}
                      setNewUploadedDocIds={setNewUploadedDocIds}
                      setDeleteDocIds={setDeleteDocIds}
                      formContext={formContext}
                    />
                  )
                : undefined,
              renderSubStatus: () => (
                <Field name={`${fieldName}.isVerified`}>
                  {({ meta }) => {
                    if (meta.error && meta.touched) return <>{meta.error}</>;
                    if (!isOld && !hasCertificate && meta.touched) return <>Upload GST Certificate</>;
                    return null;
                  }}
                </Field>
              ),
              renderStatus: () => {
                if (isJobProcessing) {
                  return <GSTProgressStatus phase="fetching" label="Fetching data" />;
                }
                if (isVerified) {
                  return <span className={gstStyles.connectedBadge}>Connected</span>;
                }
                if (isInActive) {
                  return <span style={{ color: '#999' }}>Inactive</span>;
                }
                const buttonText =
                  sessionStatus === 'otp_pending' || sessionStatus === 'user_name_required'
                    ? 'Verify with OTP'
                    : 'Connect';
                return (
                  <MeritoButton
                    type="secondary-2"
                    size="md"
                    btnText={buttonText}
                    onClick={() => setConnectModalIndex(index)}
                  />
                );
              },
              renderRowDetail: () => (
                <GstAccountDetail
                  fieldName={fieldName}
                  gstAccountIndex={index}
                  formCompleteStatus={formCompleteStatus}
                  gstApiErrors={gstApiErrors}
                  setGstApiErrors={setGstApiErrors}
                  formContext={formContext}
                  formType={formType}
                  requiresCertPassword={certRequiresPassword}
                  certificateFileDetails={statement?.gstCertificate}
                  onSubmitCertPassword={(password: string) =>
                    sendFileJobToApi(UnderwritingSteps.company, certFieldName, {
                      ...certJob,
                      fileMetaData: { ...certJob?.fileMetaData, password },
                      status: uploadStates.doneUploading,
                    })
                  }
                />
              ),
            };
          });

          return (
            <>
              <GSTAccountList records={records} />
              <div className={styles.bottomBtnContainer}>
                <MeritoButton
                  size='md2'
                  type='secondary-1'
                  icon={<AddIcon />}
                  onClick={() => setConnectModalIndex(-1)}
                  btnText="Add GST Account"
                />
              </div>

              {connectModalIndex !== null && (
                <LendingConnectGst
                  onClose={closeConnectModal}
                  onConnected={async (values) => {
                    const credential = await getCredentialForGstin(values.gstin);
                    const alreadyPresent = (fields.value || []).some((s: any) => s?.gstin === values.gstin);
                    if (connectModalIndex === -1 && !alreadyPresent) {
                      // New account — push a new row with the connected values
                      fields.push({
                        ...gstDetailInitialState,
                        credentialId: credential?.credentialId ?? null,
                        gstin: values.gstin,
                        username: credential?.gstUsername || values.username,
                        jobId: values.jobId,
                        statementJobId: values.statementJobId,
                        sessionStatus: credential?.sessionStatus,
                        isVerified: credential?.sessionStatus === "active",
                      });
                    } else if (connectModalIndex !== -1) {
                      // Existing account — update in place
                      changeForm(`gstStatements[${connectModalIndex}].credentialId`, credential?.credentialId ?? null);
                      changeForm(`gstStatements[${connectModalIndex}].gstin`, values?.gstin);
                      changeForm(`gstStatements[${connectModalIndex}].username`, credential?.gstUsername || values?.username);
                      changeForm(`gstStatements[${connectModalIndex}].jobId`, values?.jobId);
                      changeForm(`gstStatements[${connectModalIndex}].statementJobId`, values?.statementJobId);
                      changeForm(`gstStatements[${connectModalIndex}].sessionStatus`, credential?.sessionStatus);
                      changeForm(`gstStatements[${connectModalIndex}].isVerified`, credential?.sessionStatus === "active");
                    }
                    if (values.jobId) {
                      setActiveGstJob({ gstin: values.gstin, fetchJobId: values.jobId });
                    }
                    // A successful connect can trigger PAN-based discovery of
                    // sibling GSTINs server-side (see fetchGSTINFromPAN in
                    // jigsaw-api). Re-sync so those show up now instead of
                    // only after a page refresh. The draft hint keeps this
                    // row from being pushed a second time by the sync.
                    await syncCredentialsFromBackend({
                      replaceIndex: connectModalIndex >= 0 ? connectModalIndex : undefined,
                      draft: { gstin: values.gstin, username: values.username },
                    });
                    (formContext as any)?.fetchGstAnalysisJobs?.();
                    setConnectModalIndex(null);
                  }}
                  initialGstin={connectModalIndex >= 0 ? statements[connectModalIndex]?.gstin : undefined}
                  initialUsername={connectModalIndex >= 0 ? statements[connectModalIndex]?.username : undefined}
                  formDetails={{
                    gstin: connectModalIndex >= 0 ? (statements[connectModalIndex]?.gstin || '') : '',
                    username: connectModalIndex >= 0 ? (statements[connectModalIndex]?.username || '') : '',
                    statementJobId: connectModalIndex >= 0 ? (statements[connectModalIndex]?.statementJobId || '') : '',
                    formType: (formContext as any)?.extraFormDetails?.formType,
                    formId: (formContext as any)?.extraFormDetails?.id,
                    monthYear: (formContext as any)?.extraFormDetails?.monthYear,
                    fieldName: connectModalIndex >= 0 ? `gstStatements[${connectModalIndex}]` : '',
                  }}
                />
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
}

export default GstDetails;
